/**
 * A workspace's rules repository (`config.rulesRepository`, §5.27, Claude
 * Design R7) on its Projects page, beside the projects it holds rules about: a
 * remote, optionally a branch, and the folder of it each project answers to
 * (`folders`, the app's Settings › Rules) — a project keyed by its remote and
 * its folder in that repository, as every machine names it. Cleared, the key
 * leaves the document. The Worker refuses a remote or a folder name that does
 * not read, by path; the field says so first.
 */
import type React from 'react';
import type { RulesRepoRef } from '@airlock/contracts/rules/repository';
import {
  REMOTE_EXAMPLE,
  projectFolderDefault,
  ruleFolderProblem,
  rulesBranchProblem,
  rulesRemoteProblem,
  withProjectFolder
} from '@airlock/contracts/rules/repository';
import type { DocProject } from '@airlock/contracts/workspace/document';
import { Card, Field, Input } from '../../../components/primitives';
import { color, font } from '../../../theme/tokens';

export function WorkspaceRulesRepo({
  value,
  projects,
  onChange,
  onBrowse
}: {
  value: RulesRepoRef | null | undefined;
  projects: readonly DocProject[];
  onChange: (next: RulesRepoRef | undefined) => void;
  onBrowse: () => void;
}): React.JSX.Element {
  const remote = value?.remote ?? '';
  const branch = value?.branch ?? '';
  const put = (nextRemote: string, nextBranch: string): void =>
    onChange(
      nextRemote.trim()
        ? { remote: nextRemote, branch: nextBranch.trim() || null, ...(value?.folders ? { folders: value.folders } : {}) }
        : undefined
    );
  const remoteProblem = remote ? rulesRemoteProblem(remote.trim()) : null;
  const branchProblem = branch ? rulesBranchProblem(branch.trim()) : null;
  return (
    <Card padding={20}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: color.ink }}>Rules repository</span>
        {value?.remote && (
          <button type="button" onClick={onBrowse} style={{ padding: 0, border: 0, background: 'none', color: color.accentText, font: 'inherit', fontSize: 13, cursor: 'pointer' }}>
            Browse its rules
          </button>
        )}
      </div>
      <p style={{ margin: '0 0 14px', fontSize: 12.5, lineHeight: 1.55, color: color.inkFaint, maxWidth: 640 }}>
        Optional. A Git repository of RFCs this workspace is held to. Each machine reads it with its own Git access.
      </p>
      <div className="al-two" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: 14 }}>
        <Field label="Remote" htmlFor="rules-remote">
          <Input
            id="rules-remote"
            value={remote}
            placeholder={REMOTE_EXAMPLE.split(' or ')[0]}
            onChange={(event) => put(event.target.value, branch)}
            style={{ fontFamily: font.mono, fontSize: 13, borderColor: remoteProblem ? color.red : undefined }}
          />
          {remoteProblem && <span style={{ display: 'block', marginTop: 6, fontSize: 12, color: color.badText }}>{remoteProblem}</span>}
        </Field>
        <Field label="Branch" htmlFor="rules-branch">
          <Input
            id="rules-branch"
            value={branch}
            disabled={!remote}
            placeholder="default branch"
            onChange={(event) => put(remote, event.target.value)}
            style={{ fontFamily: font.mono, fontSize: 13, borderColor: branchProblem ? color.red : undefined }}
          />
          {branchProblem && <span style={{ display: 'block', marginTop: 6, fontSize: 12, color: color.badText }}>{branchProblem}</span>}
        </Field>
      </div>
      {value?.remote && projects.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: color.ink, marginBottom: 4 }}>Folder of each project</div>
          <p style={{ margin: '0 0 10px', fontSize: 12.5, lineHeight: 1.55, color: color.inkFaint, maxWidth: 640 }}>
            An RFC in web/ holds in the project named web, in api+web/ in those two, at the top level in every repository. Left empty, a project takes the
            name shown, which moves if its remote or its folder is renamed; a name written here stays.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.4fr) minmax(0,1fr)', gap: '8px 14px', alignItems: 'center' }}>
            {projects.map((project) => {
              const given =
                value.folders?.find((one) => one.remote === project.remote && (one.subPath ?? '') === (project.subPath ?? ''))?.folder ?? '';
              const problem = given.trim() ? ruleFolderProblem(given.trim()) : null;
              return [
                <span key={`${project.id}:n`} style={{ fontSize: 13.5, color: color.ink, overflowWrap: 'anywhere' }}>
                  {project.name}
                </span>,
                <span key={`${project.id}:w`} style={{ fontFamily: font.mono, fontSize: 12, color: color.inkFaint, overflowWrap: 'anywhere' }}>
                  {project.remote}
                  {project.subPath ? ` › ${project.subPath}` : ''}
                </span>,
                <span key={`${project.id}:f`}>
                  <Input
                    aria-label={`Folder of ${project.name}`}
                    value={given}
                    placeholder={projectFolderDefault(project.remote, project.subPath) ?? 'no default'}
                    onChange={(event) => onChange(withProjectFolder(value, project.remote, project.subPath, event.target.value))}
                    style={{ fontFamily: font.mono, fontSize: 13, borderColor: problem ? color.red : undefined }}
                  />
                  {problem && <span style={{ display: 'block', marginTop: 6, fontSize: 12, color: color.badText }}>{problem}</span>}
                </span>
              ];
            })}
          </div>
        </div>
      )}
    </Card>
  );
}
